import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { X, AlertTriangle, Pin } from 'lucide-react';
import { DEFAULT_TOAST_MS, useToastStore, type Toast } from '../store/toastStore';

/** Maximum number of cards rendered in the stack. Toasts beyond this index
 *  are queued in the store but hidden until they advance. Keeping this
 *  small (~4) preserves the visual "deck of cards" silhouette. */
const STACK_MAX_VISIBLE = 4;

/**
 * Stack of transient notifications rendered at the bottom-right of the
 * window as a deck-of-cards stack. ONLY the front card (toasts[0]) has an
 * active auto-dismiss timer; when it dismisses, the next toast advances to
 * the front and its timer arms. Sticky toasts have no timer and hold up
 * the queue until the user manually closes them.
 *
 * Hovering anywhere over the stack:
 *   - pauses the front card's timer (resumes on mouseleave with the
 *     remaining time, so a brief hover doesn't reset the countdown)
 *   - reveals a transparent "Clear all" button above the deck. It clears
 *     every NON-sticky toast (sticky prompts stay).
 *
 * Accessibility: the host is an ARIA-live region (`role="status"`,
 * `aria-live="polite"`). Error-kind toasts upgrade to `assertive`. Only
 * the front card is screen-reader-visible; back cards are aria-hidden.
 */
export function ToastHost(): ReactElement | null {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);
  const dismissTransient = useToastStore((s) => s.dismissTransient);
  const [hovered, setHovered] = useState(false);

  const top = toasts[0] as Toast | undefined;
  // Per-card "ms remaining" — tracked in a ref because it has no visual
  // mapping. Reset whenever the front toast changes; decremented when the
  // timer is paused for hover and re-armed on resume.
  const remainingRef = useRef<number | null>(null);
  // The top toast id at the time `remainingRef` was last reset. Keeps the
  // reset effect idempotent if the front card object identity changes
  // without its `id` changing (e.g. zustand re-creating arrays).
  const remainingForId = useRef<number | null>(null);

  // Reset the remaining counter when a new toast advances to the front.
  useEffect(() => {
    if (!top) {
      remainingRef.current = null;
      remainingForId.current = null;
      return;
    }
    if (remainingForId.current === top.id) return;
    remainingForId.current = top.id;
    const isSticky = top.sticky === true || top.ms === 0;
    remainingRef.current = isSticky ? null : top.ms ?? DEFAULT_TOAST_MS;
  }, [top]);

  // Arm / disarm the auto-dismiss timer for the front toast. On pause
  // (hover) the cleanup decrements `remainingRef` by the elapsed slice so
  // resume picks up where we left off, not from a fresh full duration.
  useEffect(() => {
    if (!top || hovered) return;
    const ms = remainingRef.current;
    if (ms == null || ms <= 0) return;
    const startedAt = Date.now();
    const handle = window.setTimeout(() => {
      remainingRef.current = null;
      dismiss(top.id);
    }, ms);
    return () => {
      window.clearTimeout(handle);
      const elapsed = Date.now() - startedAt;
      if (remainingRef.current != null) {
        remainingRef.current = Math.max(0, remainingRef.current - elapsed);
      }
    };
  }, [top, hovered, dismiss]);

  const onClearAll = useCallback(() => {
    dismissTransient();
  }, [dismissTransient]);

  if (toasts.length === 0) return null;

  const visible = toasts.slice(0, STACK_MAX_VISIBLE);
  const transientCount = toasts.filter((t) => !t.sticky && t.ms !== 0).length;
  const showClearAll = transientCount > 1;

  return (
    <div
      className="mp-toast-host"
      role="status"
      aria-live="polite"
      aria-atomic="false"
      data-hovered={hovered ? '' : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {showClearAll && (
        <button
          type="button"
          className="mp-toast-clear-all"
          aria-label="Clear all transient notifications"
          title="Clear all (keeps sticky)"
          onClick={onClearAll}
          // Keep out of the tab order until the user is actually
          // interacting with the stack — otherwise a Tab through the
          // page would land here unexpectedly.
          tabIndex={hovered ? 0 : -1}
        >
          Clear all
        </button>
      )}
      <div className="mp-toast-stack" data-count={Math.min(toasts.length, STACK_MAX_VISIBLE)}>
        {visible.map((t, i) => {
          const isFront = i === 0;
          const isSticky = t.sticky === true || t.ms === 0;
          return (
            <div
              key={t.id}
              className="mp-toast"
              data-kind={t.kind}
              data-stack-index={i}
              data-front={isFront ? '' : undefined}
              data-sticky={isSticky ? '' : undefined}
              aria-hidden={isFront ? undefined : true}
              aria-live={isFront && t.kind === 'error' ? 'assertive' : undefined}
              style={{ ['--stack-i' as string]: String(i) } as React.CSSProperties}
            >
              {t.kind === 'error' ? (
                <AlertTriangle size={14} aria-hidden className="mp-toast__icon" />
              ) : isSticky ? (
                <Pin size={13} aria-hidden className="mp-toast__icon" />
              ) : null}
              <div className="mp-toast__body">
                <span id={`mp-toast-${t.id}-msg`} className="mp-toast__msg">
                  {t.message}
                </span>
                {isFront && t.actions && t.actions.length > 0 && (
                  <div
                    className="mp-toast__actions"
                    role="group"
                    aria-labelledby={`mp-toast-${t.id}-msg`}
                  >
                    {t.actions.map((a, idx) => (
                      <button
                        key={`${t.id}-action-${idx}`}
                        type="button"
                        className="mp-toast__action"
                        data-primary={a.primary ? '' : undefined}
                        onClick={a.onClick}
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {isFront && (
                <button
                  type="button"
                  className="mp-toast__close"
                  aria-label="Dismiss notification"
                  title="Dismiss"
                  onClick={() => dismiss(t.id)}
                >
                  <X size={12} aria-hidden />
                </button>
              )}
              {/* Front, non-sticky cards get a thin progress edge that
                  drains over the toast's `ms`. CSS-only animation —
                  paused via `animation-play-state` when the host is
                  hovered, in lock-step with the JS timer above so the
                  bar and the actual dismissal always agree. */}
              {isFront && !isSticky && (
                <div
                  className="mp-toast__progress"
                  aria-hidden
                  style={
                    {
                      ['--mp-toast-duration' as string]: `${t.ms ?? DEFAULT_TOAST_MS}ms`,
                    } as React.CSSProperties
                  }
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
